'use client';

import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useMemo,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '../../../lib/utils';
import { Button, type ButtonProps } from '../../button/button';
import { CheckboxIndicator } from '../../checkbox/checkbox/checkbox-indicator';
import { Icon, type SvgIcon } from '../../icon/icon';
import { RadioIndicator } from '../../radio/radio/radio-indicator';
import {
  MENU_ICON_SIZE,
  menuItemVariants,
  menuHeadingVariants,
  menuDividerVariants,
  type MenuItemVariantsProps,
} from '../shared/menu-item-variants';
import {
  MenuDescriptionItemText,
  menuDescriptionItemVariants,
  type MenuDescriptionItemTextProps,
} from '../shared/menu-description-item';
import { useMenuSize } from '../shared/menu-size-context';
import { useDrawerMenuContext } from './drawer-menu-context';

// children is restated because react-i18next widens it in the consuming apps
type DrawerMenuRowProps = Omit<ComponentPropsWithoutRef<'button'>, 'type' | 'children'> & {
  children?: ReactNode;
};

export type DrawerMenuItemProps = DrawerMenuRowProps & {
  /** Render the row as the single child element, e.g. an `<a href>`. */
  asChild?: boolean;
  icon?: SvgIcon;
  variant?: MenuItemVariantsProps['variant'];
  /** @default true */
  closeOnClick?: boolean;
};

export const DrawerMenuItem = forwardRef<HTMLButtonElement, DrawerMenuItemProps>(
  (
    {
      asChild = false,
      className,
      children,
      icon,
      variant = 'default',
      closeOnClick = true,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { size } = useMenuSize();
    const { closeFromItem } = useDrawerMenuContext();
    const Comp = asChild ? Slot : 'button';

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);

      if (closeOnClick) {
        closeFromItem();
      }
    };

    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : 'button'}
        data-slot="drawer-menu-item"
        className={cn(menuItemVariants({ size, variant }), 'text-left', className)}
        onClick={handleClick}
        {...props}>
        {icon ? <Icon icon={icon} size={MENU_ICON_SIZE[size]} /> : null}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
DrawerMenuItem.displayName = 'DrawerMenuItem';

export type DrawerMenuDescriptionItemProps = Omit<
  DrawerMenuItemProps,
  'asChild' | 'icon' | 'title' | 'children'
> &
  Pick<MenuDescriptionItemTextProps, 'title' | 'description' | 'classNames' | 'children'>;

/**
 * Two-line row: a title with a description under it, clamped to two lines, plus
 * optional content below such as a Tag. No icon or chevron.
 */
export const DrawerMenuDescriptionItem = forwardRef<
  HTMLButtonElement,
  DrawerMenuDescriptionItemProps
>(({ className, title, description, classNames, children, ...props }, ref) => {
  const { size } = useMenuSize();
  const descriptionId = useId();

  return (
    <DrawerMenuItem
      ref={ref}
      data-slot="drawer-menu-description-item"
      aria-describedby={description ? descriptionId : undefined}
      className={cn(menuDescriptionItemVariants({ size }), className)}
      {...props}>
      <MenuDescriptionItemText
        size={size}
        title={title}
        description={description}
        descriptionId={descriptionId}
        classNames={classNames}>
        {children}
      </MenuDescriptionItemText>
    </DrawerMenuItem>
  );
});
DrawerMenuDescriptionItem.displayName = 'DrawerMenuDescriptionItem';

export type DrawerMenuCheckboxItemProps = DrawerMenuRowProps & {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** @default false */
  closeOnClick?: boolean;
};

export const DrawerMenuCheckboxItem = forwardRef<HTMLButtonElement, DrawerMenuCheckboxItemProps>(
  (
    {
      className,
      children,
      checked = false,
      onCheckedChange,
      closeOnClick = false,
      disabled,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { size } = useMenuSize();
    const { closeFromItem } = useDrawerMenuContext();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      onCheckedChange?.(!checked);

      if (closeOnClick) {
        closeFromItem();
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        data-checked={checked ? '' : undefined}
        data-slot="drawer-menu-checkbox-item"
        className={cn(menuItemVariants({ size }), 'text-left', className)}
        onClick={handleClick}
        {...props}>
        <CheckboxIndicator checked={checked} disabled={disabled} size={size} />
        {children}
      </button>
    );
  },
);
DrawerMenuCheckboxItem.displayName = 'DrawerMenuCheckboxItem';

type DrawerMenuRadioGroupContextValue = {
  value?: string;
  onValueChange?: (value: string) => void;
};

const DrawerMenuRadioGroupContext = createContext<DrawerMenuRadioGroupContextValue>({});

export type DrawerMenuRadioGroupProps = ComponentPropsWithoutRef<'div'> &
  DrawerMenuRadioGroupContextValue;

export const DrawerMenuRadioGroup = ({
  className,
  value,
  onValueChange,
  ...props
}: DrawerMenuRadioGroupProps) => {
  const contextValue = useMemo(() => ({ value, onValueChange }), [value, onValueChange]);

  return (
    <DrawerMenuRadioGroupContext.Provider value={contextValue}>
      <div
        role="radiogroup"
        data-slot="drawer-menu-radio-group"
        className={cn('flex flex-col gap-0.5', className)}
        {...props}
      />
    </DrawerMenuRadioGroupContext.Provider>
  );
};

export type DrawerMenuRadioItemProps = DrawerMenuRowProps & {
  value: string;
  /** @default false */
  closeOnClick?: boolean;
};

export const DrawerMenuRadioItem = forwardRef<HTMLButtonElement, DrawerMenuRadioItemProps>(
  ({ className, children, value, closeOnClick = false, disabled, onClick, ...props }, ref) => {
    const { size } = useMenuSize();
    const { closeFromItem } = useDrawerMenuContext();
    const group = useContext(DrawerMenuRadioGroupContext);
    const checked = group.value === value;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      group.onValueChange?.(value);

      if (closeOnClick) {
        closeFromItem();
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={checked}
        disabled={disabled}
        data-checked={checked ? '' : undefined}
        data-slot="drawer-menu-radio-item"
        className={cn(menuItemVariants({ size }), 'text-left', className)}
        onClick={handleClick}
        {...props}>
        <RadioIndicator checked={checked} disabled={disabled} size={size} />
        {children}
      </button>
    );
  },
);
DrawerMenuRadioItem.displayName = 'DrawerMenuRadioItem';

export type DrawerMenuGroupProps = ComponentPropsWithoutRef<'div'>;

export const DrawerMenuGroup = ({ className, ...props }: DrawerMenuGroupProps) => (
  <div
    role="group"
    data-slot="drawer-menu-group"
    className={cn('flex flex-col gap-0.5', className)}
    {...props}
  />
);

export type DrawerMenuHeadingProps = ComponentPropsWithoutRef<'div'>;

export const DrawerMenuHeading = ({ className, ...props }: DrawerMenuHeadingProps) => {
  const { size } = useMenuSize();

  return (
    <div
      data-slot="drawer-menu-heading"
      className={cn(menuHeadingVariants({ size }), className)}
      {...props}
    />
  );
};

export type DrawerMenuDividerProps = ComponentPropsWithoutRef<'div'>;

export const DrawerMenuDivider = ({ className, ...props }: DrawerMenuDividerProps) => {
  const { size } = useMenuSize();

  return (
    <div
      role="separator"
      data-slot="drawer-menu-divider"
      className={cn(menuDividerVariants({ size }), className)}
      {...props}
    />
  );
};

export type DrawerMenuFooterProps = ComponentPropsWithoutRef<'div'>;

/** Pinned row of buttons. Place it next to `DrawerMenuContent`, not inside it. */
export const DrawerMenuFooter = ({ className, ...props }: DrawerMenuFooterProps) => (
  <div
    data-slot="drawer-menu-footer"
    className={cn('mt-auto flex shrink-0 gap-3 px-5 pt-2 pb-5 [&>*]:flex-1', className)}
    {...props}
  />
);

export type DrawerMenuButtonProps = DrawerMenuRowProps &
  Pick<ButtonProps, 'variant' | 'icon' | 'iconRight' | 'className'> & {
    /** @default true */
    closeOnClick?: boolean;
  };

export const DrawerMenuButton = forwardRef<HTMLButtonElement, DrawerMenuButtonProps>(
  (
    {
      className,
      children,
      variant = 'primary',
      icon,
      iconRight,
      closeOnClick = true,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { closeFromItem } = useDrawerMenuContext();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);

      if (closeOnClick) {
        closeFromItem();
      }
    };

    return (
      <Button
        ref={ref}
        type="button"
        size="standard"
        variant={variant}
        icon={icon}
        iconRight={iconRight}
        data-slot="drawer-menu-button"
        className={cn('w-full', className)}
        onClick={handleClick}
        {...props}>
        {children}
      </Button>
    );
  },
);
DrawerMenuButton.displayName = 'DrawerMenuButton';
